// Desktop Dev Chat Section - Admin-only subpage for testing node-llama-cpp
// Manager centralizes API calls; section holds React state and wires handlers

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { MainLayout } from '../../../../components/Layout';
import { LoadingPanel } from '../../../../components';
import { DesktopHeader } from '../../components';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNavigationManager } from '../../../../managers/navigation-manager';
import { useManager, logger } from '../../../../core';
import { DesktopDevChatManager } from './managers/desktop-dev-chat-manager';
import { DesktopDevChatInterface } from './components';
import {
  getDevChatFrontendToolDefinitionList,
  executeDevChatFrontendTool,
  formatDevChatFrontendToolBubbleSummary,
  getDevChatFrontendToolById,
} from './tools/registry';
import './DesktopChatSection.css';

function logDevChatPersistFailure(err) {
  logger.error(
    { tags: 'section|desktop|dev-chat|persist', color1: 'yellow' },
    'Failed to save dev chat options:',
    err
  );
}

function applyContextMetricsFromModelInfo(modelInfo, setMetrics) {
  if (!modelInfo) {
    setMetrics(null);
    return;
  }
  const runtime = modelInfo.contextSize;
  const train = modelInfo.trainContextSize;
  const gpuL = modelInfo.gpuLayers;
  const totalL = modelInfo.totalLayers;
  const r = runtime != null && runtime !== '' ? Number(runtime) : null;
  const t = train != null && train !== '' ? Number(train) : null;
  const g = gpuL != null && gpuL !== '' ? Number(gpuL) : null;
  const tl = totalL != null && totalL !== '' ? Number(totalL) : null;
  const metrics = {
    runtime: r != null && !Number.isNaN(r) ? r : null,
    train: t != null && !Number.isNaN(t) ? t : null,
    gpuLayers: g != null && !Number.isNaN(g) ? g : null,
    totalLayers: tl != null && !Number.isNaN(tl) ? tl : null,
  };
  const hasAny =
    metrics.runtime != null ||
    metrics.train != null ||
    metrics.gpuLayers != null ||
    metrics.totalLayers != null;
  if (!hasAny) {
    setMetrics(null);
    return;
  }
  setMetrics(metrics);
}

const isDesktopEnvironment = () => {
  return typeof window !== 'undefined' && !!window.navigationAPI;
};

const DESKTOP_DEV_CHAT_SELECTED_MODEL_KEY = 'desktop-dev-chat-selected-model';

function getInitialSelectedModelId() {
  try {
    if (typeof window === 'undefined') return '';
    const s = localStorage.getItem(DESKTOP_DEV_CHAT_SELECTED_MODEL_KEY);
    return s && String(s).trim() ? String(s).trim() : '';
  } catch {
    return '';
  }
}

/**
 * Stable-enough client ids for streamed assistant rows in Dev Chat.
 */
function createStreamMessageId(prefix = 'msg') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getResponseChunkKind(payload) {
  if (payload?.chunkType === 'segment') {
    return `segment:${payload.segmentType || 'unknown'}`;
  }
  return 'text';
}

function getResponseChunkMessageSegmentType(payload) {
  if (payload?.chunkType === 'segment') {
    return payload.segmentType || 'unknown';
  }
  return 'text';
}

function getToolStreamMessageId(payload) {
  const runId = payload?.runId || 'run-unknown';
  const callIndex = payload?.callIndex ?? -1;
  return `dev-tool-${runId}-${callIndex}`;
}

function getSystemPromptBubbleId(chatId) {
  return chatId ? `system-prompt-${chatId}` : 'system-prompt-new';
}

function getSystemPromptBubbleContent(systemPrompt) {
  const t = typeof systemPrompt === 'string' ? systemPrompt : '';
  return t.trim().length > 0 ? t : '(Model default system prompt)';
}

function safeToString(value) {
  if (typeof value === 'string') return value;
  try {
    if (value == null) return '';
    return String(value);
  } catch {
    return '';
  }
}

function tryJsonStringify(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return safeToString(value);
  }
}

function formatToolResultSummary(toolId, result) {
  return formatDevChatFrontendToolBubbleSummary(toolId, result);
}

function chatHistoryToMessages(chatArray, systemPromptText, chatId) {
  const systemPromptBubble = {
    id: getSystemPromptBubbleId(chatId),
    role: 'assistant',
    content: getSystemPromptBubbleContent(systemPromptText),
    segmentType: 'system_prompt',
  };

  const out = [];
  let assistantTextAppendableId = null; // last assistant bubble id for segmentType='text'
  let assistantTextLastKind = null; // 'text' only for merging

  const pushAssistantText = (text) => {
    const t = safeToString(text);
    if (t.trim().length === 0) return; // loaded chats shouldn't produce empty bubbles
    if (
      assistantTextAppendableId &&
      assistantTextLastKind === 'text' &&
      out.length > 0 &&
      out[out.length - 1]?.id === assistantTextAppendableId
    ) {
      const last = out[out.length - 1];
      out[out.length - 1] = { ...last, content: (last.content || '') + t };
      return;
    }
    const id = `seg-load-text-${chatId}-${out.length}`;
    assistantTextAppendableId = id;
    assistantTextLastKind = 'text';
    out.push({
      id,
      role: 'assistant',
      content: t,
      segmentType: 'text',
    });
  };

  const pushAssistantSegment = (segmentType, text) => {
    const t = safeToString(text);
    if (t.trim().length === 0) return;
    assistantTextAppendableId = null;
    assistantTextLastKind = null;
    const st = safeToString(segmentType) || 'unknown';
    out.push({
      id: `seg-load-${chatId}-${out.length}-${st}`,
      role: 'assistant',
      content: t,
      segmentType: st,
    });
  };

  let toolCallIndex = 0;
  const pushToolCall = ({ functionName, params, result }) => {
    toolCallIndex += 1;
    const toolStatus = 'completed';
    out.push({
      id: `dev-tool-load-${chatId}-${toolCallIndex}`,
      role: 'assistant',
      segmentType: 'dev_tool_call',
      toolCallIndex: toolCallIndex - 1,
      functionName: safeToString(functionName),
      paramsText: tryJsonStringify(params),
      toolStatus,
      resultSummary: formatToolResultSummary(safeToString(functionName), result),
    });
    // Following assistant text should not merge with a tool bubble.
    assistantTextAppendableId = null;
    assistantTextLastKind = null;
  };

  if (!Array.isArray(chatArray)) return [systemPromptBubble];

  for (const entry of chatArray) {
    if (!entry || entry.type === 'system') continue; // system bubble comes from persisted systemPromptText
    if (entry.type === 'user') {
      const content = safeToString(entry.text);
      if (content.trim().length === 0) continue;
      out.push({
        id: `user-load-${chatId}-${out.length}`,
        role: 'user',
        content,
      });
      assistantTextAppendableId = null;
      assistantTextLastKind = null;
      continue;
    }

    if (entry.type === 'model') {
      const response = Array.isArray(entry.response) ? entry.response : [];
      for (const piece of response) {
        if (typeof piece === 'string') {
          pushAssistantText(piece);
          continue;
        }
        if (!piece || typeof piece !== 'object') continue;

        if (piece.type === 'segment') {
          pushAssistantSegment(piece.segmentType, piece.text);
          continue;
        }

        if (piece.type === 'functionCall') {
          pushToolCall({
            functionName: piece.name,
            params: piece.params,
            result: piece.result,
          });
          continue;
        }
      }
    }
  }

  return [systemPromptBubble, ...out];
}

export function DesktopDevChatSection() {
  const { manager, isInitialized } = useManager(DesktopDevChatManager);
  const { isAdmin, membershipDetails, user } = useAuth();
  const navigationManager = useNavigationManager();

  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(getInitialSelectedModelId);
  const [modelState, setModelState] = useState('idle'); // idle | loading | loaded | unloading
  const [loadProgress, setLoadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusType, setStatusType] = useState('info'); // 'info' | 'success' | 'error'
  /** Context tokens, layer offload/total from isModelLoaded modelInfo when a model is loaded */
  const [contextMetrics, setContextMetrics] = useState(null);

  const [systemPrompt, setSystemPrompt] = useState('');
  const [showSystemPromptEditor, setShowSystemPromptEditor] = useState(true);
  const [messages, setMessages] = useState(() => [
    {
      id: getSystemPromptBubbleId(null),
      role: 'assistant',
      content: getSystemPromptBubbleContent(''),
      segmentType: 'system_prompt',
    },
  ]); // [{ id, role, content, segmentType?, segmentId? }]
  const [isGenerating, setIsGenerating] = useState(false);
  const [messageQueue, setMessageQueue] = useState([]); // Messages to send when generation completes
  /** Active streamed assistant response row, keyed by logical chunk kind (`text`, `segment:thought`, etc.). */
  const activeAssistantResponseRef = useRef(null);
  const GENERATING_PLACEHOLDER_ID = 'generating-placeholder';

  const insertBeforeGeneratingPlaceholder = useCallback((prev, row, placeholderId) => {
    const phIdx = prev.findIndex((m) => m.id === placeholderId);
    if (phIdx === -1) return [...prev, row];
    const next = [...prev];
    next.splice(phIdx, 0, row);
    return next;
  }, []);
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [contextSizeMode, setContextSizeMode] = useState('auto'); // 'auto' | 'manual'
  const [contextSizeMin, setContextSizeMin] = useState('2048');
  const [contextSizeMax, setContextSizeMax] = useState('4096');
  const [contextAllocationMode, setContextAllocationMode] = useState('auto'); // 'auto' | 'manual'
  const [contextAllocationSize, setContextAllocationSize] = useState('4096');
  const [defaultContextFlashAttention, setDefaultContextFlashAttention] = useState(false);
  /** tool name -> enabled (default true) */
  const [devChatToolsEnabled, setDevChatToolsEnabled] = useState(() => {
    const initial = {};
    for (const t of getDevChatFrontendToolDefinitionList()) {
      if (t?.name) initial[t.name] = true;
    }
    return initial;
  });
  const devChatToolDefinitions = useMemo(
    () => getDevChatFrontendToolDefinitionList(),
    []
  );
  const pendingToolInteractionsRef = useRef(new Map());
  const activeFrontendToolExecutionsRef = useRef(new Set());
  const frontendToolInstancesRef = useRef(new Map());
  const toolParamsTextSoFarRef = useRef(new Map());

  const selectedModel = useMemo(
    () => models.find((m) => m.id === selectedModelId),
    [models, selectedModelId]
  );

  // Load saved dev chat options when model selection changes
  useEffect(() => {
    if (!selectedModelId || !models.length) return;
    const model = selectedModel;
    const opts = model?.metadata?.devChatOptions;
    const defaults = {
      contextSizeMode: 'auto',
      contextSizeMin: '2048',
      contextSizeMax: '4096',
      contextAllocationMode: 'auto',
      contextAllocationSize: '4096',
      defaultContextFlashAttention: false,
      // chatKnobs preserved for schema compatibility; Dev Chat UI no longer edits thinking (see backend comments).
      chatKnobs: { enableThinking: true },
    };
    const merged = opts ? { ...defaults, ...opts, chatKnobs: { ...defaults.chatKnobs, ...(opts.chatKnobs || {}) } } : defaults;
    setContextSizeMode(merged.contextSizeMode);
    setContextSizeMin(String(merged.contextSizeMin));
    setContextSizeMax(String(merged.contextSizeMax));
    setContextAllocationMode(merged.contextAllocationMode);
    setContextAllocationSize(String(merged.contextAllocationSize));
    setDefaultContextFlashAttention(
      merged.defaultContextFlashAttention !== undefined && merged.defaultContextFlashAttention !== null
        ? !!merged.defaultContextFlashAttention
        : merged.contextAllocationMode === 'manual'
    );
  }, [selectedModelId, models, selectedModel]);

  const getDevChatOptions = useCallback(
    (overrides = {}) => {
      const base = {
        contextSizeMode,
        contextSizeMin,
        contextSizeMax,
        contextAllocationMode,
        contextAllocationSize,
        defaultContextFlashAttention,
        chatKnobs: { enableThinking: true },
      };
      const merged = { ...base, ...overrides };
      merged.chatKnobs = { ...base.chatKnobs, ...(overrides.chatKnobs || {}) };
      return merged;
    },
    [
      contextSizeMode,
      contextSizeMin,
      contextSizeMax,
      contextAllocationMode,
      contextAllocationSize,
      defaultContextFlashAttention,
    ]
  );

  const persistDevChatOptions = useCallback(
    (overrides = {}) => {
      if (!selectedModelId || typeof window === 'undefined' || !window.modelsAPI?.setDevChatOptions) return;
      const opts = getDevChatOptions(overrides);
      window.modelsAPI.setDevChatOptions(selectedModelId, opts).then(() => {
        setModels((prev) =>
          prev.map((m) =>
            m.id === selectedModelId
              ? { ...m, metadata: { ...(m.metadata || {}), devChatOptions: opts } }
              : m
          )
        );
      }).catch(logDevChatPersistFailure);
    },
    [selectedModelId, getDevChatOptions]
  );

  const refreshModels = useCallback(() => {
    if (manager) {
      const list = manager.getModels();
      setModels(list);
    }
  }, [manager]);

  useEffect(() => {
    if (isInitialized && manager) {
      refreshModels();
    }
  }, [isInitialized, manager, refreshModels]);

  const enabledDevChatFunctionDefinitions = useMemo(() => {
    if (!devChatToolDefinitions.length) return [];
    return devChatToolDefinitions.filter((d) => d?.name && devChatToolsEnabled[d.name] !== false);
  }, [devChatToolDefinitions, devChatToolsEnabled]);

  const handleDevChatToolToggle = useCallback((toolName, enabled) => {
    setDevChatToolsEnabled((prev) => ({ ...prev, [toolName]: enabled }));
  }, []);

  const refreshChats = useCallback(async () => {
    if (typeof window === 'undefined' || !window.devChatStorageAPI) return;
    try {
      const [chatsList, activeId] = await Promise.all([
        window.devChatStorageAPI.getChats(),
        window.devChatStorageAPI.getActiveChatId(),
      ]);
      setChats(Array.isArray(chatsList) ? chatsList : []);
      setActiveChatId(activeId || null);
    } catch (_) {
      setChats([]);
      setActiveChatId(null);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      refreshChats();
    }
  }, [isInitialized, refreshChats]);

  const handleSelectChat = useCallback(
    async (chatId) => {
      if (!chatId || !window.devChatStorageAPI) return;
      try {
        const result = await window.devChatStorageAPI.loadChatIntoSession(chatId);
        if (result?.success) {
          setActiveChatId(chatId);
          const chatObj = await window.devChatStorageAPI.getChat(chatId);
          const chatArr = Array.isArray(chatObj?.chat) ? chatObj.chat : [];
          const promptText = typeof chatObj?.systemPrompt === 'string' ? chatObj.systemPrompt : '';
          setSystemPrompt(promptText);
          const nonSystemCount = chatArr.filter((x) => x?.type && x.type !== 'system').length;
          setShowSystemPromptEditor(nonSystemCount === 0);
          setMessages(chatHistoryToMessages(chatArr, promptText, chatId));
          refreshChats();
        }
      } catch (_) {}
    },
    [refreshChats]
  );

  const handleNewChat = useCallback(async () => {
    if (!window.devChatStorageAPI) return;
    try {
      await window.devChatStorageAPI.setActiveChatId(null);
      setActiveChatId(null);
      setSystemPrompt('');
      setShowSystemPromptEditor(true);
      setMessages(chatHistoryToMessages([], '', null));
      if (manager?.setChatHistory) {
        await manager.setChatHistory([]);
      }
      refreshChats();
    } catch (_) {}
  }, [refreshChats, manager]);

  const handleDeleteChat = useCallback(
    async (chatId) => {
      if (!chatId || !window.devChatStorageAPI) return;
      const wasActive = activeChatId === chatId;
      try {
        await window.devChatStorageAPI.deleteChat(chatId);
        const [chatsList, activeId] = await Promise.all([
          window.devChatStorageAPI.getChats(),
          window.devChatStorageAPI.getActiveChatId(),
        ]);
        setChats(Array.isArray(chatsList) ? chatsList : []);
        setActiveChatId(activeId || null);
        if (wasActive) {
          if (!activeId) {
            setSystemPrompt('');
            setShowSystemPromptEditor(true);
            setMessages(chatHistoryToMessages([], '', null));
            if (manager?.setChatHistory) {
              await manager.setChatHistory([]);
            }
          } else {
            const result = await window.devChatStorageAPI.loadChatIntoSession(activeId);
            if (result?.success) {
              const chatObj = await window.devChatStorageAPI.getChat(activeId);
              const chatArr = Array.isArray(chatObj?.chat) ? chatObj.chat : [];
              const promptText = typeof chatObj?.systemPrompt === 'string' ? chatObj.systemPrompt : '';
              setSystemPrompt(promptText);
              const nonSystemCount = chatArr.filter((x) => x?.type && x.type !== 'system').length;
              setShowSystemPromptEditor(nonSystemCount === 0);
              setMessages(chatHistoryToMessages(chatArr, promptText, activeId));
            }
          }
        }
      } catch (_) {}
    },
    [activeChatId, manager]
  );

  // If stored model no longer exists in the list, clear selection
  useEffect(() => {
    if (!selectedModelId || !models.length) return;
    const exists = models.some((m) => m.id === selectedModelId);
    if (!exists) {
      setSelectedModelId('');
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(DESKTOP_DEV_CHAT_SELECTED_MODEL_KEY);
        }
      } catch (_) {}
    }
  }, [models, selectedModelId]);

  const handleSelectModel = useCallback((id) => {
    setSelectedModelId(id);
    try {
      if (typeof localStorage !== 'undefined') {
        if (id) {
          localStorage.setItem(DESKTOP_DEV_CHAT_SELECTED_MODEL_KEY, id);
        } else {
          localStorage.removeItem(DESKTOP_DEV_CHAT_SELECTED_MODEL_KEY);
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (isInitialized && manager) {
      manager.isModelLoaded().then((res) => {
        if (res?.isLoaded) {
          setModelState('loaded');
          setStatusMessage('Model ready');
          setStatusType('success');
          applyContextMetricsFromModelInfo(res?.modelInfo, setContextMetrics);
        }
      });
    }
  }, [isInitialized, manager]);

  const removePlaceholder = useCallback(
    (prev) => prev.filter((m) => m.id !== GENERATING_PLACEHOLDER_ID),
    [GENERATING_PLACEHOLDER_ID]
  );

  const getPendingToolKey = useCallback((runId, callIndex) => {
    const r = typeof runId === 'string' ? runId : 'run-unknown';
    const c = typeof callIndex === 'number' ? callIndex : -1;
    return `${r}:${c}`;
  }, []);

  const updateToolMessageRow = useCallback((runId, callIndex, updater) => {
    const id = getToolStreamMessageId({ runId, callIndex });
    setMessages((prev) => {
      const withoutPlaceholder = removePlaceholder(prev);
      const idx = withoutPlaceholder.findIndex((m) => m.id === id);
      if (idx === -1) {
        const baseRow = {
          id,
          role: 'assistant',
          segmentType: 'dev_tool_call',
          toolCallIndex: typeof callIndex === 'number' ? callIndex : 0,
          toolCallRunId: typeof runId === 'string' ? runId : 'run-unknown',
          functionName: 'tool',
          paramsText: '',
          toolStatus: 'executing',
          toolUi: null,
        };
        const row = typeof updater === 'function' ? updater(baseRow) : { ...baseRow, ...updater };
        return [...withoutPlaceholder, row];
      }
      const next = [...withoutPlaceholder];
      const row = next[idx];
      next[idx] = typeof updater === 'function' ? updater({ ...row }) : { ...row, ...updater };
      return next;
    });
  }, [removePlaceholder]);

  const requestToolInteraction = useCallback(
    ({ runId, callIndex, functionName }, toolUi) =>
      new Promise((resolve) => {
        const key = getPendingToolKey(runId, callIndex);
        pendingToolInteractionsRef.current.set(key, { resolve });
        updateToolMessageRow(runId, callIndex, (row) => ({
          ...row,
          functionName:
            row.functionName && row.functionName !== 'tool'
              ? row.functionName
              : (functionName || 'tool'),
          toolCallRunId: row.toolCallRunId || (typeof runId === 'string' ? runId : 'run-unknown'),
          toolCallIndex:
            typeof row.toolCallIndex === 'number'
              ? row.toolCallIndex
              : (typeof callIndex === 'number' ? callIndex : 0),
          toolStatus: 'waiting_user',
          toolUi,
        }));
      }),
    [getPendingToolKey, updateToolMessageRow]
  );

  const handleToolInteractionAction = useCallback(
    ({ runId, callIndex, actionId }) => {
      const key = getPendingToolKey(runId, callIndex);
      const pending = pendingToolInteractionsRef.current.get(key);
      if (!pending) return;
      pendingToolInteractionsRef.current.delete(key);
      pending.resolve({ actionId });
      updateToolMessageRow(runId, callIndex, (row) => ({
        ...row,
        toolStatus: 'executing',
        toolUi: null,
      }));
    },
    [getPendingToolKey, updateToolMessageRow]
  );

  useEffect(() => {
    if (!manager?.onGenerateStream) return;
    const cleanup = manager.onGenerateStream((payload) => {
      const { type } = payload;
      if (type === 'response-chunk') {
        const text = typeof payload.text === 'string' ? payload.text : '';
        const isVisibleText = text.trim().length > 0;
        const chunkKind = getResponseChunkKind(payload);
        const messageSegmentType = getResponseChunkMessageSegmentType(payload);

        // Avoid creating "empty" bubbles from whitespace-only deltas.
        // If we already have an active bubble for this chunk kind, we still append whitespace
        // so we don't lose formatting mid-stream.
        if (isVisibleText) {
          setMessages((prev) => {
            const withoutPlaceholder = removePlaceholder(prev);
            const active = activeAssistantResponseRef.current;
            if (active && active.kind === chunkKind) {
              const idx = withoutPlaceholder.findIndex((m) => m.id === active.messageId);
              if (idx !== -1) {
                const next = [...withoutPlaceholder];
                const row = next[idx];
                next[idx] = { ...row, content: (row.content || '') + text };
                return next;
              }
            }

            const id = createStreamMessageId('seg');
            activeAssistantResponseRef.current = { messageId: id, kind: chunkKind };
            return [
              ...withoutPlaceholder,
              {
                id,
                role: 'assistant',
                content: text,
                segmentType: messageSegmentType,
              },
            ];
          });
        }

        if (!isVisibleText) {
          // Whitespace-only delta: append only if we're already streaming into a bubble.
          const active = activeAssistantResponseRef.current;
          if (active && active.kind === chunkKind) {
            setMessages((prev) => {
              const idx = prev.findIndex((m) => m.id === active.messageId);
              if (idx === -1) return prev;
              const next = [...prev];
              const row = next[idx];
              next[idx] = { ...row, content: (row.content || '') + text };
              return next;
            });
          }
        }

        if (payload.isSegmentEnd) {
          const active = activeAssistantResponseRef.current;
          if (active && active.kind === chunkKind) {
            activeAssistantResponseRef.current = null;
          }
        }
      } else if (type === 'function-call-params-chunk') {
        activeAssistantResponseRef.current = null;
        const { runId, callIndex, functionName, paramsChunk, done } = payload;
        const id = getToolStreamMessageId(payload);
        const piece = typeof paramsChunk === 'string' ? paramsChunk : '';

        const execKey = getPendingToolKey(runId, callIndex);
        const prevParamsText = toolParamsTextSoFarRef.current.get(execKey) || '';
        const nextParamsTextSoFar = prevParamsText + piece;
        toolParamsTextSoFarRef.current.set(execKey, nextParamsTextSoFar);

        // Allow tool classes to stream-live-update the tool bubble while arguments are being generated.
        const ToolClass = getDevChatFrontendToolById(functionName);
        const toolInstance = ToolClass ? frontendToolInstancesRef.current.get(execKey) : null;
        if (ToolClass) {
          const inst = toolInstance || new ToolClass();
          if (!toolInstance) {
            frontendToolInstancesRef.current.set(execKey, inst);
          }

          if (typeof inst.onParamsChunk === 'function') {
            const updateToolUiInRow = (toolUi) => {
              setMessages((prev) => {
                const withoutPlaceholder = removePlaceholder(prev);
                const idx = withoutPlaceholder.findIndex((m) => m.id === id);
                if (idx === -1) return prev;
                const next = [...withoutPlaceholder];
                next[idx] = { ...next[idx], toolUi };
                return next;
              });
            };

            const clearToolUiInRow = () => updateToolUiInRow(null);

            try {
              // Intentionally fire-and-forget: UI preview should not block argument accumulation.
              inst.onParamsChunk(
                { paramsChunk: piece, paramsTextSoFar: nextParamsTextSoFar, done, functionName, runId, callIndex },
                { updateToolUi: updateToolUiInRow, clearToolUi: clearToolUiInRow }
              );
            } catch (e) {
              // Ignore preview errors; execution summary will still reflect the final tool result.
            }
          }
        }

        setMessages((prev) => {
          const withoutPlaceholder = removePlaceholder(prev);
          const idx = withoutPlaceholder.findIndex((m) => m.id === id);
          if (idx === -1) {
            const row = {
              id,
              role: 'assistant',
              segmentType: 'dev_tool_call',
              toolCallIndex: callIndex,
              toolCallRunId: typeof runId === 'string' ? runId : 'run-unknown',
              functionName: functionName || 'unknown',
              paramsText: piece,
              toolStatus: done ? 'executing' : 'accumulating',
            };
            if (done) {
              toolParamsTextSoFarRef.current.delete(execKey);
              frontendToolInstancesRef.current.delete(execKey);
            }
            return insertBeforeGeneratingPlaceholder(withoutPlaceholder, row, GENERATING_PLACEHOLDER_ID);
          }
          const next = [...withoutPlaceholder];
          const row = { ...next[idx] };
          if (piece.length > 0) {
            row.paramsText = (row.paramsText || '') + piece;
          }
          if (functionName) {
            row.functionName = functionName;
          }
          if (!row.toolCallRunId && typeof runId === 'string') {
            row.toolCallRunId = runId;
          }
          if (done) {
            row.toolStatus = 'executing';

            // Streaming preview is complete; drop the accumulated text + instance.
            toolParamsTextSoFarRef.current.delete(execKey);
            frontendToolInstancesRef.current.delete(execKey);
          }
          next[idx] = row;
          return next;
        });
      } else if (type === 'tool-call-executing') {
        activeAssistantResponseRef.current = null;
        const id = getToolStreamMessageId(payload);
        const runId = payload?.runId;
        setMessages((prev) => {
          const withoutPlaceholder = removePlaceholder(prev);
          const idx = withoutPlaceholder.findIndex((m) => m.id === id);
          if (idx === -1) {
            return [
              ...withoutPlaceholder,
              {
                id,
                role: 'assistant',
                segmentType: 'dev_tool_call',
                toolCallIndex: payload.callIndex,
                toolCallRunId: typeof runId === 'string' ? runId : 'run-unknown',
                functionName: payload.functionName || 'unknown',
                paramsText: '',
                toolStatus: 'executing',
              },
            ];
          }
          const next = [...withoutPlaceholder];
          const row = next[idx];
          if (row.toolStatus !== 'completed' && row.toolStatus !== 'failed') {
            next[idx] = {
              ...row,
              toolStatus: 'executing',
              toolCallRunId: row.toolCallRunId || (typeof runId === 'string' ? runId : row.toolCallRunId),
            };
          }
          return next;
        });
      } else if (type === 'tool-call-complete') {
        activeAssistantResponseRef.current = null;
        const { runId, callIndex, ok, summary } = payload;
        const id = getToolStreamMessageId(payload);
        setMessages((prev) => {
          const withoutPlaceholder = removePlaceholder(prev);
          const idx = withoutPlaceholder.findIndex((m) => m.id === id);
          if (idx === -1) {
            return [
              ...withoutPlaceholder,
              {
                id,
                role: 'assistant',
                segmentType: 'dev_tool_call',
                toolCallIndex: callIndex,
                toolCallRunId: typeof runId === 'string' ? runId : 'run-unknown',
                functionName: payload.functionName || 'unknown',
                paramsText: '',
                toolStatus: ok ? 'completed' : 'failed',
                resultSummary: typeof summary === 'string' ? summary : '',
              },
            ];
          }
          const next = [...withoutPlaceholder];
          const row = next[idx];
          next[idx] = {
            ...row,
            toolStatus: ok ? 'completed' : 'failed',
            resultSummary: typeof summary === 'string' ? summary : '',
            toolCallRunId: row.toolCallRunId || (typeof runId === 'string' ? runId : row.toolCallRunId),
          };
          return next;
        });
      } else if (type === 'done') {
        activeAssistantResponseRef.current = null;
        setIsGenerating(false);
        setMessages((prev) => removePlaceholder(prev));
        refreshChats();
      } else if (type === 'error') {
        activeAssistantResponseRef.current = null;
        setIsGenerating(false);
        refreshChats();
        const errMsg = payload.error || 'Unknown error';
        const stack = payload.stack || '';
        const id = createStreamMessageId('err');
        setMessages((prev) => [
          ...removePlaceholder(prev),
          {
            id,
            role: 'assistant',
            content: errMsg,
            segmentType: 'error',
            errorStack: stack,
          },
        ]);
      }
    });
    return cleanup;
  }, [
    manager,
    refreshChats,
    insertBeforeGeneratingPlaceholder,
    getPendingToolKey,
    removePlaceholder,
  ]);

  useEffect(() => {
    if (!manager?.onToolCallDispatch || !manager?.toolExecutionComplete) return;
    const cleanup = manager.onToolCallDispatch((payload) => {
      const runId = typeof payload?.runId === 'string' ? payload.runId : 'run-unknown';
      const callIndex = typeof payload?.callIndex === 'number' ? payload.callIndex : -1;
      const functionName = typeof payload?.functionName === 'string' ? payload.functionName : 'unknown';
      const params = payload?.params && typeof payload.params === 'object' ? payload.params : {};
      const execKey = getPendingToolKey(runId, callIndex);
      if (activeFrontendToolExecutionsRef.current.has(execKey)) {
        return;
      }
      activeFrontendToolExecutionsRef.current.add(execKey);

      Promise.resolve()
        .then(async () => {
          updateToolMessageRow(runId, callIndex, (row) => ({
            ...row,
            functionName,
            toolCallRunId: row.toolCallRunId || runId,
            toolCallIndex:
              typeof row.toolCallIndex === 'number' ? row.toolCallIndex : callIndex,
          }));

          const result = await executeDevChatFrontendTool(functionName, params, {
            runId,
            callIndex,
            functionName,
            requestInteraction: (toolUi) =>
              requestToolInteraction({ runId, callIndex, functionName }, toolUi),
            updateToolUi: (toolUi) =>
              updateToolMessageRow(runId, callIndex, (row) => ({ ...row, toolUi })),
            clearToolUi: () =>
              updateToolMessageRow(runId, callIndex, (row) => ({ ...row, toolUi: null })),
          });

          const summary = formatDevChatFrontendToolBubbleSummary(functionName, result);
          await manager.toolExecutionComplete({
            runId,
            callIndex,
            ok: true,
            result,
            summary,
          });
        })
        .catch(async (err) => {
          const message = err?.message || String(err);
          updateToolMessageRow(runId, callIndex, (row) => ({
            ...row,
            toolUi: null,
          }));
          await manager.toolExecutionComplete({
            runId,
            callIndex,
            ok: false,
            error: message,
            summary: message,
          });
        })
        .finally(() => {
          activeFrontendToolExecutionsRef.current.delete(execKey);
        });
    });
    return cleanup;
  }, [manager, getPendingToolKey, requestToolInteraction, updateToolMessageRow]);

  const handleLoad = useCallback(async () => {
    if (!manager || !selectedModelId || modelState !== 'idle') return false;
    setModelState('loading');
    setLoadProgress(0);
    setStatusMessage(null);
    setContextMetrics(null);
    const cleanup = manager.onLoadProgress((progress) => {
      const p = typeof progress === 'number' ? progress : progress?.progress ?? 0;
      const percent = p <= 1 ? Math.round(p * 100) : Math.round(p);
      setLoadProgress(Math.min(100, percent));
    });

    const completeWithHold = (loaded, message, type) => {
      setLoadProgress(100);
      setModelState(loaded ? 'loaded' : 'idle');
      setStatusMessage(message);
      setStatusType(type);
      setTimeout(() => setLoadProgress(0), 500);
    };

    const loadOptions = {
      contextSize:
        contextSizeMode === 'auto'
          ? 'auto'
          : {
              min: Math.max(512, parseInt(contextSizeMin, 10) || 2048),
              max: Math.min(128000, parseInt(contextSizeMax, 10) || 4096),
            },
      contextAllocationSize:
        contextAllocationMode === 'auto'
          ? undefined
          : (parseInt(contextAllocationSize, 10) || undefined),
      defaultContextFlashAttention,
    };

    try {
      const result = await manager.loadModel(selectedModelId, loadOptions);
      const fromResult = result?.success === true;
      if (fromResult) {
        const loadedInfo = await manager.isModelLoaded();
        applyContextMetricsFromModelInfo(loadedInfo?.modelInfo, setContextMetrics);
        completeWithHold(true, 'Model loaded', 'success');
        return true;
      } else {
        const check = await manager.isModelLoaded();
        const loaded = check?.isLoaded;
        const errMsg = result?.error || 'Load failed';
        if (typeof console !== 'undefined' && console.error) {
          console.error('[DesktopDevChat] Load failed:', errMsg, result);
        }
        completeWithHold(loaded, errMsg, 'error');
        return false;
      }
    } catch (err) {
      const check = await manager.isModelLoaded();
      const loaded = check?.isLoaded;
      const errMsg = err?.message || 'Load failed';
      if (typeof console !== 'undefined' && console.error) {
        console.error('[DesktopDevChat] Load error:', errMsg, err);
      }
      completeWithHold(loaded, errMsg, 'error');
      return false;
    } finally {
      cleanup();
    }
  }, [
    manager,
    selectedModelId,
    modelState,
    contextSizeMode,
    contextSizeMin,
    contextSizeMax,
    contextAllocationMode,
    contextAllocationSize,
    defaultContextFlashAttention,
  ]);

  // Keep the system prompt bubble content in sync with the editable system prompt text.
  useEffect(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m?.segmentType === 'system_prompt'
          ? { ...m, content: getSystemPromptBubbleContent(systemPrompt) }
          : m
      )
    );
  }, [systemPrompt]);

  const sendMessageNow = useCallback(
    async (trimmed) => {
      if (!trimmed || !selectedModelId || !manager) return false;
      const addUserMessage = () => {
        const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : createStreamMessageId('msg');
        setMessages((prev) => [...prev, { id, role: 'user', content: trimmed }]);
      };
      if (modelState !== 'loaded') {
        const loaded = await handleLoad();
        if (!loaded) return false;
      }
      addUserMessage();
      setShowSystemPromptEditor(false);
      activeAssistantResponseRef.current = null;
      setIsGenerating(true);
      setMessages((prev) => [
        ...prev,
        {
          id: GENERATING_PLACEHOLDER_ID,
          role: 'assistant',
          content: '',
          segmentType: 'generating',
        },
      ]);
      // Ensure chat system prompt is persisted so future loads render and generate consistently.
      try {
        const api = window?.devChatStorageAPI;
        if (api && typeof api.updateChatSystemPrompt === 'function') {
          let chatId = activeChatId;
          if (!chatId && typeof api.createChat === 'function') {
            const created = await api.createChat();
            chatId = created?.id;
            setActiveChatId(chatId || null);
          }
          if (chatId) {
            await api.updateChatSystemPrompt(chatId, systemPrompt);
          }
        }
      } catch (_) {}

      const genOpts = {
        ...(enabledDevChatFunctionDefinitions.length > 0
          ? { functions: enabledDevChatFunctionDefinitions }
          : {}),
        ...(systemPrompt.trim().length > 0 ? { systemPrompt } : {}),
      };
      manager
        .generate(trimmed, genOpts)
        .catch((err) => {
        setIsGenerating(false);
        setMessages((prev) => prev.filter((m) => m.id !== GENERATING_PLACEHOLDER_ID));
        if (typeof console !== 'undefined' && console.error) {
          console.error('[DesktopDevChat] Generate error:', err);
        }
      });
      return true;
    },
    [selectedModelId, modelState, handleLoad, manager, enabledDevChatFunctionDefinitions, activeChatId, systemPrompt]
  );

  const handleSendMessage = useCallback(
    async (message) => {
      if (!message?.trim() || !selectedModelId || !manager) return false;
      const trimmed = message.trim();
      if (isGenerating) {
        setMessageQueue((prev) => [...prev, trimmed]);
        return true;
      }
      return sendMessageNow(trimmed);
    },
    [isGenerating, selectedModelId, manager, sendMessageNow]
  );

  const handleRemoveFromQueue = useCallback((index) => {
    setMessageQueue((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Process queue when generation completes (reserve isGenerating immediately to prevent re-entry)
  useEffect(() => {
    if (!isGenerating && messageQueue.length > 0) {
      setIsGenerating(true);
      const [next, ...rest] = messageQueue;
      setMessageQueue(rest);
      sendMessageNow(next);
    }
  }, [isGenerating, messageQueue, sendMessageNow]);

  const handleCancelGenerate = useCallback(() => {
    if (manager?.cancelGenerate) {
      manager.cancelGenerate();
    }
  }, [manager]);

  const handleUnload = useCallback(async () => {
    if (!manager || modelState !== 'loaded') return;
    setModelState('unloading');
    setStatusMessage(null);
    setContextMetrics(null);
    try {
      await manager.unloadModel();
      setModelState('idle');
    } catch (err) {
      setModelState('loaded');
      setStatusMessage(err?.message || 'Unload failed');
      setStatusType('error');
    }
  }, [manager, modelState]);

  const isAdminForDesktopDevChat =
    !!isAdmin ||
    user?.role === 'admin' ||
    membershipDetails?.role === 'admin' ||
    membershipDetails?.role === 'site_admin';

  // Admin-only: redirect non-admins to desktop settings
  useEffect(() => {
    if (!isAdminForDesktopDevChat) {
      navigationManager.navigateTo('desktop/settings');
    }
  }, [isAdminForDesktopDevChat, navigationManager]);

  // Redirect if not in desktop environment
  useEffect(() => {
    if (typeof window !== 'undefined' && !isDesktopEnvironment()) {
      window.location.href = '/';
    }
  }, []);

  if (!isAdminForDesktopDevChat) {
    return null;
  }

  if (!isDesktopEnvironment()) {
    return (
      <MainLayout>
        <div className="desktop-access-denied">
          <div className="desktop-access-content">
            <h2>Desktop Only</h2>
            <p>This section is only available in the Revolt Desktop application.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!isInitialized) {
    return (
      <div className="desktop-layout">
        <DesktopHeader activePage="dev-chat" />
        <MainLayout contentClassName="main-content--desktop main-content--desktop-dev-chat">
          <div className="desktop-dev-chat-container">
            <LoadingPanel
              title="Loading Dev Chat"
              subtitle="Initializing..."
              showAppName={false}
            />
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div className="desktop-layout">
      <DesktopHeader activePage="dev-chat" />
      <MainLayout contentClassName="main-content--desktop main-content--desktop-dev-chat">
        <div className="desktop-dev-chat-container">
          <div className="desktop-dev-chat-max-width-wrapper">
            <DesktopDevChatInterface
              messages={messages}
              isGenerating={isGenerating}
              messageQueue={messageQueue}
              onRemoveFromQueue={handleRemoveFromQueue}
              onCancelGenerate={handleCancelGenerate}
              chats={chats}
              activeChatId={activeChatId}
              onSelectChat={handleSelectChat}
              onCreateChat={handleNewChat}
              onDeleteChat={handleDeleteChat}
              models={models}
              selectedModelId={selectedModelId}
              modelState={modelState}
              loadProgress={loadProgress}
              statusMessage={statusMessage}
              statusType={statusType}
              contextRuntimeTokens={contextMetrics?.runtime ?? null}
              contextTrainTokens={contextMetrics?.train ?? null}
              layerGpuCount={contextMetrics?.gpuLayers ?? null}
              layerTotalCount={contextMetrics?.totalLayers ?? null}
              onSelectModel={handleSelectModel}
              onLoad={handleLoad}
              onUnload={handleUnload}
              contextSizeMode={contextSizeMode}
              contextSizeMin={contextSizeMin}
              contextSizeMax={contextSizeMax}
              contextAllocationMode={contextAllocationMode}
              contextAllocationSize={contextAllocationSize}
              defaultContextFlashAttention={defaultContextFlashAttention}
              onContextSizeModeChange={(v) => {
                setContextSizeMode(v);
                persistDevChatOptions({ contextSizeMode: v });
              }}
              onContextSizeMinChange={setContextSizeMin}
              onContextSizeMaxChange={setContextSizeMax}
              onContextAllocationModeChange={(v) => {
                setContextAllocationMode(v);
                persistDevChatOptions({ contextAllocationMode: v });
              }}
              onContextAllocationSizeChange={setContextAllocationSize}
              onDefaultContextFlashAttentionChange={(v) => {
                setDefaultContextFlashAttention(v);
                persistDevChatOptions({ defaultContextFlashAttention: v });
              }}
              onContextOptionsCommit={persistDevChatOptions}
              devChatToolDefinitions={devChatToolDefinitions}
              devChatToolsEnabled={devChatToolsEnabled}
              onDevChatToolToggle={handleDevChatToolToggle}
              onSendMessage={handleSendMessage}
              onToolInteractionAction={handleToolInteractionAction}
              showSystemPromptEditor={showSystemPromptEditor}
              systemPrompt={systemPrompt}
              onSystemPromptChange={setSystemPrompt}
              devChatManager={manager}
            />
          </div>
        </div>
      </MainLayout>
    </div>
  );
}
